import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { CheckCircle2, Loader2, PauseCircle } from "lucide-react";
import { VaultSync } from "@forgepoint/shared";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getVaultSyncDisplayState } from "./vaultSyncsStore";

const JUST_NOW_THRESHOLD_MS = 60_000;
const RELATIVE_TIME_REFRESH_MS = 60_000;

/** Label text that hides when the Vault container measures narrow, leaving the icon. */
const COLLAPSIBLE_LABEL_CLASS = "hidden @[640px]/vault:inline";

interface SyncedFolderStatusBadgeProps {
  sync: VaultSync;
}

/**
 * Sync state shown beside a synced folder's name: paused, error, syncing, or last synced time.
 * @param props - The sync to describe
 * @returns The status badge element
 */
export function SyncedFolderStatusBadge({
  sync,
}: SyncedFolderStatusBadgeProps) {
  const now = useMinuteClock();
  const state = getVaultSyncDisplayState(sync);

  if (state === "paused") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
        <PauseCircle className="size-3.5" aria-label="Paused" />
        <span className={COLLAPSIBLE_LABEL_CLASS}>Paused</span>
      </span>
    );
  }

  if (state === "error") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-destructive">
            <span
              role="img"
              className="size-2 rounded-full bg-destructive"
              aria-label="Sync error"
            />
            <span className={COLLAPSIBLE_LABEL_CLASS}>Sync error</span>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {sync.progress?.lastError}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (state === "syncing") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-label="Syncing" />
        <span className={COLLAPSIBLE_LABEL_CLASS}>Syncing</span>
      </span>
    );
  }

  const syncedLabel = `Synced ${formatSyncedAgo(sync.progress?.lastSuccessAt ?? null, now)}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5" aria-label={syncedLabel} />
          <span className={COLLAPSIBLE_LABEL_CLASS}>{syncedLabel}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm break-all">
        {sync.integrationConfig.webUrl}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Re-renders the caller once a minute so relative times stay current.
 * @returns The current time, refreshed every minute
 */
function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(
      () => setNow(Date.now()),
      RELATIVE_TIME_REFRESH_MS,
    );
    return () => clearInterval(interval);
  }, []);

  return now;
}

/**
 * Formats how long ago a sync last succeeded, e.g. "just now" or "5 minutes ago".
 * @param lastSuccessAt - ISO time of the last successful run
 * @param now - The current time in milliseconds
 * @returns The relative time phrase
 */
function formatSyncedAgo(lastSuccessAt: string | null, now: number): string {
  if (!lastSuccessAt) {
    return "just now";
  }
  const syncedAt = new Date(lastSuccessAt);
  if (now - syncedAt.getTime() < JUST_NOW_THRESHOLD_MS) {
    return "just now";
  }
  return `${formatDistanceToNow(syncedAt)} ago`;
}
